import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Megaphone,
  Users,
  Building2,
  BarChart3,
  Settings,
  Bell,
  Upload,
  User,
  FileCheck,
  FileText,
  Menu,
  X,
  Workflow,
  Database,
  Mail,
  ScrollText,
  ChevronDown,
  Search,
  Sparkles,
  Home,
  Lightbulb,
  LifeBuoy,
  Monitor,
  Sun,
  Moon,
} from 'lucide-react';
import type { ThemePreference } from '@socialsoup/shared';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { useOptionalTheme } from '@/app/ThemeProvider';
import { PORTAL_CONFIG, type PortalRole } from '@/lib/constants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { NotificationBellDropdown } from '@/components/layout/NotificationBellDropdown';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface TopNavBarProps {
  role: PortalRole;
}

/**
 * theme-customization B2 — the APPEARANCE OPTIONS, in the order they are offered.
 *
 * Which of them actually render is decided by the server, not by this table: the picker keeps
 * only the entries the snapshot's `supportedModes` publishes, so a version-1 answer (`['LIGHT']`)
 * renders no picker at all and a future capability change needs no edit here.
 */
const THEME_OPTIONS: readonly { value: ThemePreference; label: string; icon: React.ElementType }[] =
  [
    { value: 'SYSTEM', label: 'System', icon: Monitor },
    { value: 'LIGHT', label: 'Light', icon: Sun },
    { value: 'DARK', label: 'Dark', icon: Moon },
  ];

const THEME_PICKER_LABEL_ID = 'topnav-appearance-label';

/**
 * Text size is the BROWSER's zoom, not an app setting: it is already per device, and an in-app
 * CSS `zoom` was probed and dropped (Floating UI double-applies it to every popper —
 * `docs/plans/text-size-preference-brief.md`). The menu only names the shortcut.
 */
const ZOOM_KEY =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl';

/** The mobile menu panel, named by the hamburger's `aria-controls` while it is mounted. */
const MOBILE_MENU_ID = 'topnav-mobile-menu';

interface NavLeaf {
  label: string;
  path: string;
  icon: React.ElementType;
  badge?: number;
}

interface NavLinkEntry extends NavLeaf {
  kind: 'link';
}

interface NavGroupEntry {
  kind: 'group';
  label: string;
  icon: React.ElementType;
  items: NavLeaf[];
}

type NavEntry = NavLinkEntry | NavGroupEntry;

/**
 * Per-role navigation, grouped into top-level links + overflow dropdowns
 * (mirrors the Stitch mockup's grouped nav). Settings lives in the avatar
 * dropdown, not the top bar — see `hasSettings` below.
 */
function getNavItems(role: PortalRole): NavEntry[] {
  const base = PORTAL_CONFIG[role].basePath;

  switch (role) {
    case 'admin':
      return [
        { kind: 'link', label: 'Review Queue', path: base, icon: ClipboardList },
        { kind: 'link', label: 'Campaigns', path: `${base}/campaigns`, icon: Megaphone },
        { kind: 'link', label: 'Find Creators', path: `${base}/creators`, icon: Search },
        {
          kind: 'group',
          label: 'Directory',
          icon: Users,
          items: [
            { label: 'Influencers', path: `${base}/influencers`, icon: Users },
            { label: 'Clients', path: `${base}/clients`, icon: Building2 },
            { label: 'Users', path: `${base}/users`, icon: User },
            { label: 'Creator Home', path: `${base}/creator-home`, icon: Home },
          ],
        },
        {
          kind: 'group',
          label: 'Data',
          icon: Database,
          items: [
            { label: 'Pipelines', path: `${base}/pipelines`, icon: Workflow },
            { label: 'Intelligence Reports', path: `${base}/intel-reports`, icon: Sparkles },
            { label: 'ClientHub', path: `${base}/clienthub`, icon: Database },
            { label: 'Analytics', path: `${base}/analytics`, icon: BarChart3 },
          ],
        },
        {
          kind: 'group',
          label: 'Comms',
          icon: Bell,
          items: [
            { label: 'Notifications', path: `${base}/notifications`, icon: Bell },
            { label: 'Email Templates', path: `${base}/email-templates`, icon: Mail },
            { label: 'Email Log', path: `${base}/email-log`, icon: ScrollText },
          ],
        },
      ];
    case 'influencer':
      // Phase 8a task 9 (design decision 9) — Dashboard, Notifications, and
      // Profile removed from the nav. All three routes stay registered for
      // direct URL access (Dashboard moves to `${base}/dashboard`); the
      // header bell keeps notification access without a nav link.
      return [
        { kind: 'link', label: 'Campaigns', path: `${base}/campaigns`, icon: Megaphone },
        {
          kind: 'group',
          label: 'Content',
          icon: FileCheck,
          items: [
            { label: 'Submissions', path: `${base}/submissions`, icon: FileCheck },
            { label: 'Upload', path: `${base}/upload`, icon: Upload },
            // concept-approval-flow Phase 3 — creator pitch/compose surface.
            { label: 'Concepts', path: `${base}/concepts`, icon: Lightbulb },
          ],
        },
      ];
    case 'client':
      return [
        // concept-approval-flow Phase 5 — the client portal's Concepts queue.
        { kind: 'link', label: 'Concepts', path: `${base}/concepts`, icon: Lightbulb },
        // Phase 6 changes navigation order only. The base route remains the
        // existing Content Review landing page until the owner decides otherwise.
        { kind: 'link', label: 'Content Review', path: base, icon: ClipboardList },
        { kind: 'link', label: 'Campaigns', path: `${base}/campaigns`, icon: Megaphone },
        // Guidelines restored 2026-08-09 — it now has a real backend
        // (guidelines-and-orphans phases 1-3); it was hidden on 2026-08-06 only
        // because the page called an endpoint that did not exist.
        //
        // Performance is retired (decision D-12, audit 2026-10-05 A-61): the
        // page is deleted and `/client/performance` redirects to the dashboard
        // with a one-time notice. There is no Performance link to restore.
        { kind: 'link', label: 'Guidelines', path: `${base}/guidelines`, icon: ScrollText },
        { kind: 'link', label: 'Approved Content', path: `${base}/approved`, icon: FileCheck },
        { kind: 'link', label: 'Notifications', path: `${base}/notifications`, icon: Bell },
      ];
    case 'agent':
      return [
        { kind: 'link', label: 'Dashboard', path: base, icon: LayoutDashboard },
        { kind: 'link', label: 'Talent', path: `${base}/talent`, icon: Users },
        { kind: 'link', label: 'Campaigns', path: `${base}/campaigns`, icon: Megaphone },
        {
          kind: 'group',
          label: 'Work',
          icon: FileCheck,
          items: [
            { label: 'Submissions', path: `${base}/submissions`, icon: FileCheck },
            { label: 'Reports', path: `${base}/reports`, icon: FileText },
          ],
        },
        { kind: 'link', label: 'Notifications', path: `${base}/notifications`, icon: Bell },
      ];
  }
}

export function TopNavBar({ role }: TopNavBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Which desktop nav group is expanded. Drives aria-expanded/aria-controls on
  // the trigger + the panel's visibility (audit 2026-07-01 #17). Opens on pointer
  // hover AND keyboard focus-within, matching the prior CSS-only behaviour but now
  // exposing disclosure state to assistive tech.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const location = useLocation();
  const { user } = useAuthStore();
  const { logout } = useAuth();
  const config = PORTAL_CONFIG[role];
  const { unreadCount } = useNotifications();

  /**
   * theme-customization B2 — THE ONE APPEARANCE PICKER, in the shared chrome.
   *
   * It lives here rather than in the admin editor because all four roles own their own
   * preference: this is the point at which a creator, a client or an agent can choose dark at
   * all. `useOptionalTheme` (not `useTheme`) because this bar is also rendered outside the
   * provider by suites that are about navigation.
   *
   * `setPreference` is non-null only once the server publishes a mode beyond light, so the
   * capability check is the presence of the writer plus the published option list — there is no
   * local flag and no second copy of the preference.
   */
  const theme = useOptionalTheme();
  const setPreference = theme?.setPreference ?? null;
  const themeOptions = theme
    ? THEME_OPTIONS.filter((option) => theme.snapshot.supportedModes.includes(option.value))
    : [];
  const showThemePicker = setPreference !== null && themeOptions.length > 1;
  const themeSave = theme?.preferenceSave ?? { pending: false, error: null };

  /**
   * THE AVATAR MENU'S OPEN STATE IS CONTROLLED, and it is controlled because of the appearance
   * picker (B2 a11y gate, H1).
   *
   * Radix closes a menu on ANY item select unless that item's `onSelect` prevents the default
   * (`@radix-ui/react-menu@2.1.16`, `handleSelect` → `onClose()`). With the default behaviour the
   * menu — and with it the "Saving…" line and the failure message — was gone before the PATCH
   * settled, so a sighted user saw the portal silently snap back on failure, and saw NOTHING at
   * all when the failed choice would not have changed the paint (SYSTEM → LIGHT on a light OS).
   * Screen-reader users were covered by the live regions below; this is the inverse gap.
   *
   * So: the options prevent the close, and this state closes the menu on SUCCESS instead — a
   * successful choice behaves like every other item here, a failed one leaves the menu open on
   * its own error and its own retry. Escape still closes at any time, pending or not, and Radix
   * returns focus to the trigger.
   */
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  /**
   * M10 — set by a route item's link click (Profile, Settings, Help), read once when the menu
   * closes. Radix returns focus to the trigger after a close, a tick after the menu unmounts; on a
   * navigating close that would take focus off the h1 the newly opened page has just focused, so
   * that one return is skipped. Escape and every other close still return focus to the trigger.
   *
   * Set only for the click react-router turns into a navigation — an unmodified primary click
   * (its `shouldProcessLinkClick`). A Ctrl/Cmd/Shift/Alt-click opens a new tab and navigates
   * nowhere, so the menu's close returns focus to the trigger as usual. Enter/Space on the item
   * arrive here too: Radix turns them into a plain `click()`, and Slot runs the link's `onClick`
   * before Radix's select-and-close.
   */
  const menuNavigatedRef = useRef(false);
  const markMenuNavigated = (event: React.MouseEvent) => {
    const modified = event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
    if (event.button === 0 && !modified) menuNavigatedRef.current = true;
  };
  /** What the polite region says once a write SETTLES. Cleared when the next one starts. */
  const [themeAnnouncement, setThemeAnnouncement] = useState('');
  const wasSavingRef = useRef(false);

  const preferenceLabel = theme
    ? (THEME_OPTIONS.find((option) => option.value === theme.snapshot.preference)?.label ?? null)
    : null;

  useEffect(() => {
    const wasSaving = wasSavingRef.current;
    wasSavingRef.current = themeSave.pending;
    if (themeSave.pending) {
      setThemeAnnouncement('');
      return;
    }
    if (!wasSaving) return;
    // SETTLED. A success confirms itself — the only other cue is a repaint, which is exactly what
    // a screen-reader user cannot perceive, leaving them waiting after "Saving…" for a message
    // that never came (B2 a11y gate, L1). A failure is announced by the assertive region and
    // shown in the menu, which stays open for it.
    if (themeSave.error === null) {
      setThemeAnnouncement(
        preferenceLabel === null ? 'Appearance saved.' : `Appearance set to ${preferenceLabel}.`,
      );
      setUserMenuOpen(false);
    }
  }, [themeSave.pending, themeSave.error, preferenceLabel]);

  // Inject the live unread badge onto the Notifications leaf, wherever it sits
  // (a top-level link for most portals, inside the admin "Comms" group).
  // TanStack Query dedupes the underlying query with NotificationBellDropdown.
  const navItems: NavEntry[] = getNavItems(role).map((entry) => {
    if (entry.kind === 'group') {
      return {
        ...entry,
        items: entry.items.map((leaf) =>
          leaf.label === 'Notifications' ? { ...leaf, badge: unreadCount || undefined } : leaf,
        ),
      };
    }
    return entry.label === 'Notifications' ? { ...entry, badge: unreadCount || undefined } : entry;
  });

  // Settings is a real route only for admin + influencer; surfaced in the avatar menu.
  const settingsPath =
    role === 'admin' || role === 'influencer' ? `${config.basePath}/settings` : null;

  // team-training-guide — Help is registered for admin (Phase 2), client (Phase 7) and
  // influencer (Phase 9); agent never. Rendered on its own, never inside the
  // `settingsPath` branch: the client portal has no Settings item.
  const helpPath =
    role === 'admin' || role === 'client' || role === 'influencer'
      ? `${config.basePath}/help`
      : null;

  // Phase 8a a11y gate: decision 2 removed Profile from the influencer top nav,
  // which left `/influencer/profile` with NO in-app entry point at all -- unlike
  // Dashboard (moved to a new address) and Notifications (reachable via the bell
  // dropdown), it had no compensating path, so it was reachable only by typing the
  // URL. That is a WCAG 2.4.5 failure ("more than one way to locate a page").
  // Influencer is the only portal with a `profile` route (`router.tsx:222`), so
  // this is scoped the same way `settingsPath` is rather than assumed universal.
  const profilePath = role === 'influencer' ? `${config.basePath}/profile` : null;

  const isLeafActive = (path: string): boolean =>
    path === config.basePath ? location.pathname === path : location.pathname.startsWith(path);

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '??';

  return (
    <>
      <header
        data-print-hidden
        className="h-topnav bg-gradient-to-r from-ss-black to-chrome-gradient-end border-b border-white/10 shadow-md flex items-center px-5 sticky top-0 z-40"
      >
        {/* Brand */}
        <div className="flex items-center gap-2.5 min-w-0 lg:shrink-0 mr-4 sm:mr-8">
          <svg
            viewBox="0 0 336.8 39.2"
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 sm:h-5 w-auto fill-white"
            aria-label="Social Soup"
            role="img"
          >
            <path d="M32.3,28.1C32.3,35,26.9,39,16.9,39C4.3,39.2,0,32.1,0,25.5h7.2c0,5.6,3.9,10.2,10,10.2c4.5,0,7.8-1.8,7.8-5.8 c0-4.2-2.7-6.4-9.1-7.5S1.3,19.8,1.3,11.6C1.3,5.3,6.6,1,16,1c9.7,0,14.7,4.9,14.7,12.6h-7.1c0-5.3-1.8-9.2-8.2-9.2 c-4.4,0-6.7,2.7-6.7,6c0,4.1,4.4,5.7,10.6,6.7C24.8,18,32.3,20.1,32.3,28.1" />
            <path d="M103.4,24.8h7.2c0,7.7-5.8,14.4-17.2,14.4c-10.8,0-18.1-7.6-18.1-19.1C75.3,9,82,1,93.3,1c11.1,0,17.2,6,17.2,14.3h-7.2 c0-5.4-3-10.9-10-10.9C84.9,4.3,82.8,13,82.8,20c0,6.8,2.6,15.6,10.6,15.6C100.7,35.7,103.4,30.2,103.4,24.8" />
            <rect x="114.3" y="1.7" width="7.2" height="36.7" />
            <path d="M145.5,1.7l15.3,36.7h-7.3L149.6,28h-14l-4.2,10.4h-7.3l15.6-36.7C139.8,1.7,145.5,1.7,145.5,1.7z M137.2,23.8h10.9 l-5.4-16.5h-0.1L137.2,23.8z" />
            <path d="M170.8,34.3h18.4v4.2h-25.5V1.7h7.2V34.3z" />
            <path d="M53.6,1C41.5,1,34.5,9,34.5,20.1c0,11.4,7.7,19.1,19.1,19.1s19-7.6,19-19.1C72.6,9,65.6,1,53.6,1 M65,22.4 c-1.8,6.6-6.5,15.1-15.5,12.8S40.1,24.1,42,17.6c1.9-6.8,6.2-15.1,15.7-12.7S66.8,15.5,65,22.4" />
            <path d="M295.9,1.7h7.2v22.1c0,10.5-5.1,15.3-15.4,15.3s-15.4-4.6-15.4-15.3V1.7h7.2v22.2c0,8.8,3,11.6,8.2,11.6s8.2-3.1,8.2-11.6 L295.9,1.7L295.9,1.7z" />
            <path d="M336.8,12.5c0,9.4-8.3,10.7-14.9,10.7h-7.7v15.3H307V1.7h14.9C328.5,1.7,336.8,3.1,336.8,12.5 M329.4,12.5 c0-3.3-2.2-6.6-7.2-6.6h-7.9V19h7.9C327.8,19,329.4,15.8,329.4,12.5" />
            <path d="M229.1,28.1c0,6.9-5.4,10.9-15.4,10.9c-12.6,0-16.9-7-16.9-13.7h7.2c0,5.6,3.9,10.2,10,10.2c4.5,0,7.8-1.8,7.8-5.8 c0-4.2-2.7-6.4-9.1-7.5c-6.4-1.1-14.7-2.6-14.7-10.7c0-6.4,5.3-10.6,14.7-10.6c9.7,0,14.7,4.9,14.7,12.6h-7.1c0-5.3-1.8-9.2-8.2-9.2 c-4.4,0-6.7,2.7-6.7,6c0,4.1,4.4,5.7,10.6,6.7C221.6,18,229.1,20.1,229.1,28.1" />
            <path d="M250.4,1c-12.1,0-19.1,8-19.1,19.1c0,11.4,7.7,19.1,19.1,19.1s19-7.6,19-19.1C269.3,9,262.3,1,250.4,1 M261.7,22.4 c-1.8,6.6-6.5,15.1-15.5,12.8s-9.3-11.1-7.4-17.6c1.9-6.8,6.2-15.1,15.7-12.7C264.1,7.2,263.6,15.5,261.7,22.4" />
          </svg>
          <span className="text-chrome-text-muted text-sm font-light">|</span>
          <span className="text-sm font-semibold truncate" style={{ color: config.accentCssValue }}>
            {config.hubName}
          </span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 flex-1" role="navigation">
          {navItems.map((entry) => {
            if (entry.kind === 'group') {
              const GroupIcon = entry.icon;
              const groupActive = entry.items.some((leaf) => isLeafActive(leaf.path));
              const groupBadge = entry.items.reduce((sum, leaf) => sum + (leaf.badge ?? 0), 0);

              // Hover/focus mega-menu (mirrors the Stitch mockup). The panel opens
              // on pointer hover AND when any descendant — including the trigger —
              // receives keyboard focus (group-focus-within), so it stays reachable
              // without a click. `pt-2` bridges the gap so the menu doesn't close
              // when the pointer travels from the trigger to the panel.
              const panelId = `nav-group-${entry.label.toLowerCase().replace(/\s+/g, '-')}`;
              const isOpen = openGroup === entry.label;
              return (
                <div
                  key={entry.label}
                  className="group/nav relative h-full flex items-center"
                  onMouseEnter={() => setOpenGroup(entry.label)}
                  onMouseLeave={() => setOpenGroup(null)}
                  onFocus={() => setOpenGroup(entry.label)}
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpenGroup(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setOpenGroup(null);
                  }}
                >
                  <button
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors relative outline-none focus-visible:ring-2 focus-visible:ring-highlight',
                      groupActive
                        ? 'font-semibold'
                        : 'text-chrome-text-muted group-hover/nav:text-on-chrome group-focus-within/nav:text-on-chrome hover:bg-chrome-bg-hover-soft',
                    )}
                    style={groupActive ? { color: config.accentCssValue } : undefined}
                  >
                    <GroupIcon className="h-3.5 w-3.5 shrink-0" />
                    <span>{entry.label}</span>
                    <ChevronDown
                      className={cn(
                        'h-3 w-3 shrink-0 opacity-70 transition-transform',
                        isOpen && 'rotate-180',
                      )}
                      aria-hidden="true"
                    />
                    {groupBadge > 0 && (
                      // concept-authority-hardening remediation v2, Phase 5
                      // (CAR-10) — was `bg-red-500` (white on #EF4444 measures
                      // 3.76:1 at this 10px size, below the 4.5:1 small-text
                      // floor). `bg-status-rejected` is the SAME existing
                      // token used everywhere else in this codebase for a
                      // rejected/error red (`tokens.css` `--status-rejected`,
                      // Carbon red-70 #A2191F since carbon-reskin Phase 3, was
                      // #B91C1C — white on it measures 7.79:1 by the same
                      // formula tokens.css's own comment uses for the
                      // reverse pairing). Every occurrence of this exact
                      // badge in this file got the same fix (5 total).
                      <span className="bg-status-rejected text-white text-[10px] rounded-full px-1 min-w-[16px] text-center font-medium">
                        {groupBadge}
                      </span>
                    )}
                    {groupActive && (
                      <span
                        className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full translate-y-[11px]"
                        style={{ backgroundColor: config.accentCssValue }}
                      />
                    )}
                  </button>
                  <div
                    id={panelId}
                    className={cn(
                      'absolute left-0 top-full z-50 pt-2',
                      isOpen ? 'block' : 'hidden',
                    )}
                  >
                    {/* theme-customization A0: this bespoke dropdown is a POPOVER surface,
                        and it now says so. It was a literal `bg-white`, which the A0 bridge
                        would have re-pointed at the card surface — correct by value, wrong by
                        role: shadcn's own menus (the Radix user menu a few lines below, every
                        `ui/select.tsx` panel) take `--popover`, and this panel is the same
                        thing rendered by hand. Both tokens are `#FFFFFF` stock, so nothing
                        repaints; what changes is which role an admin edit moves. */}
                    <div className="w-56 rounded-lg border border-border bg-popover py-1 shadow-lg">
                      {entry.items.map((leaf) => {
                        const LeafIcon = leaf.icon;
                        const leafActive = isLeafActive(leaf.path);
                        return (
                          <NavLink
                            key={leaf.path}
                            to={leaf.path}
                            end={leaf.path === config.basePath}
                            // carbon-reskin Phase 3 gate (F3). These panel items are the WHITE
                            // surface inside the dark `<header>`, and they had
                            // `focus:outline-none` with nothing put back: (0,2,0) in the
                            // utilities layer beats the global `:focus-visible` outline at
                            // (0,1,0) in base, so the outline was suppressed and the only
                            // remaining cue was the `focus:bg-surface-light` tint — 1.05:1.
                            // They take the LIGHT-surface ring (`ring-ring`, black), never the
                            // lime one: lime on white is 1.16:1. That is the sentence the
                            // dark-bar comment below makes about this file, now true of it.
                            className={cn(
                              'flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-surface-light focus:bg-surface-light outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                              leafActive
                                ? 'font-semibold text-content-primary'
                                : 'text-content-secondary',
                            )}
                          >
                            <LeafIcon className="h-4 w-4 shrink-0 text-content-muted" />
                            <span className="flex-1">{leaf.label}</span>
                            {leaf.badge !== undefined && leaf.badge > 0 && (
                              <span className="bg-status-rejected text-white text-[10px] rounded-full px-1 min-w-[16px] text-center font-medium">
                                {leaf.badge}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            const Icon = entry.icon;
            const isActive = isLeafActive(entry.path);

            return (
              <NavLink
                key={entry.path}
                to={entry.path}
                // a11y: end=true for the base-path item so React Router's aria-current
                // uses exact matching, matching our visual isActive logic. Without this,
                // aria-current="page" would appear on the base item on every sub-page
                // (WCAG 4.1.2 / accessibility-patterns §6).
                end={entry.path === config.basePath}
                // THE DARK-BAR FOCUS RING. carbon-reskin Phase 2 R3c/S2 gave these four
                // controls a component ring at all — the group trigger `:289`, this nav link,
                // the avatar-menu trigger and the mobile hamburger. Without one they fell
                // through to `globals.css`'s global `outline: 2px solid hsl(var(--ring))`,
                // drawn over this bar's `--ss-black` (`#1a1a1a`): 1.20:1 (1.14:1 before R2
                // repointed `--ring`, so not a regression — but R2's claim that `--ring`
                // clears 3:1 on every surface is false here, and this bar is a surface the
                // registry never listed). Phase 2 used 40%-alpha white, which composites to
                // `#767676` on `#1a1a1a` = 3.83:1 and `#818186` on the gradient's far stop
                // `#2D2D35` = 3.52:1 — over the 1.4.11 line, but only just.
                // carbon-reskin Phase 3 (R2, role 6) makes it the lime highlight, which is
                // the theme's focus colour on dark: `#D4FF00` is 15.00:1 on `#1a1a1a` and
                // 11.76:1 on `--chrome-gradient-end`. Recomputed in this edit, truncated
                // toward zero. (theme-customization A0 turned that far stop from an arbitrary
                // `#2D2D35` stop utility into that token; the value did not move. The retired
                // utility is described rather than quoted: this file is inside Tailwind's
                // content glob, so writing it would mint a dead rule into the stylesheet.)
                // SCOPE IS BY EXPLICIT CLASS, NOT BY DESCENDANT SELECTOR — this `<header>`
                // also contains the WHITE dropdown panel a few lines above, now painted from
                // `--popover` rather than a literal — and lime on white is 1.16:1.
                // Those keep the light-surface `--ring` (Phase 2 R2).
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors relative outline-none focus-visible:ring-2 focus-visible:ring-highlight',
                  isActive
                    ? 'font-semibold'
                    : 'text-chrome-text-muted hover:text-on-chrome hover:bg-chrome-bg-hover-soft',
                )}
                style={isActive ? { color: config.accentCssValue } : undefined}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span>{entry.label}</span>
                {entry.badge !== undefined && entry.badge > 0 && (
                  <span className="bg-status-rejected text-white text-[10px] rounded-full px-1 min-w-[16px] text-center font-medium">
                    {entry.badge}
                  </span>
                )}
                {/* Active indicator bar */}
                {isActive && (
                  <span
                    className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full translate-y-[11px]"
                    style={{ backgroundColor: config.accentCssValue }}
                  />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Right: bell + user */}
        <div className="flex min-w-0 items-center gap-2 ml-auto">
          <NotificationBellDropdown portal={role} />

          <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                // R3c/S2 — same repair as the nav links above (3.83:1 on `#1a1a1a`).
                className="flex min-w-0 items-center gap-2 rounded-md p-1 hover:bg-chrome-bg-hover transition-colors outline-none focus-visible:ring-2 focus-visible:ring-highlight"
              >
                <Avatar className="h-7 w-7">
                  {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
                  <AvatarFallback
                    className="text-[10px] font-semibold"
                    // concept-authority-hardening remediation v2, Phase 5
                    // (CAR-10) — a new node found while touching this file's
                    // OTHER known nodes (Rule 10 amendment, declared in the
                    // phase implementer's report): the `role === 'admin'`
                    // ternary meant client/influencer/agent all rendered
                    // WHITE initials on their own light accent fill (client
                    // 2.48:1, influencer 2.68:1, agent 2.72:1 — all below the
                    // 3:1 WCAG 1.4.11 floor). All four portal accents are
                    // light, so all four now take the SAME dark text the
                    // admin branch already used — the ternary is gone, not
                    // recoloured.
                    // theme-customization A2a moved that dark text off
                    // `--ss-black` (the editable `chrome` role this very bar
                    // paints itself from) onto `--on-accent-initials`. Same
                    // #1A1A1A; the point is that the bar's own colour and the
                    // colour of initials sitting on a PORTAL ACCENT are two
                    // different pairs and must not share one editable role.
                    // This is the file's ONLY on-accent-initials site: the five
                    // unread badges are white on --status-rejected and the five
                    // nav labels are --on-chrome, both unchanged.
                    style={{
                      backgroundColor: config.accentCssValue,
                      color: 'var(--on-accent-initials)',
                    }}
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate text-sm font-medium text-chrome-text hidden lg:block">
                  {user?.name ?? 'User'}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-48"
              onCloseAutoFocus={(event) => {
                if (menuNavigatedRef.current) event.preventDefault();
                menuNavigatedRef.current = false;
              }}
            >
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-content-muted">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {profilePath && (
                <DropdownMenuItem
                  asChild
                  className="focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <NavLink
                    to={profilePath}
                    onClick={markMenuNavigated}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <User className="h-4 w-4 shrink-0 text-content-muted" />
                    <span>Profile</span>
                  </NavLink>
                </DropdownMenuItem>
              )}
              {settingsPath && (
                <DropdownMenuItem
                  asChild
                  className="focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <NavLink
                    to={settingsPath}
                    onClick={markMenuNavigated}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Settings className="h-4 w-4 shrink-0 text-content-muted" />
                    <span>Settings</span>
                  </NavLink>
                </DropdownMenuItem>
              )}
              {helpPath && (
                <DropdownMenuItem
                  asChild
                  className="focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <NavLink
                    to={helpPath}
                    onClick={markMenuNavigated}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <LifeBuoy className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
                    <span>Help</span>
                  </NavLink>
                </DropdownMenuItem>
              )}
              {/* theme-customization B2 — the appearance radio group.
                  Radix gives each option `role="menuitemradio"` with `aria-checked`, arrow-key
                  movement inside the group and Enter/Space selection; Escape closes the menu and
                  returns focus to the avatar trigger. The checked state carries a SHAPE cue (the
                  indicator dot) as well as the bold label, so it is not conveyed by colour. The
                  focus ring is the light-surface black `--ring`, never the lime one — this panel
                  is the WHITE popover inside the dark bar, and lime on white is 1.16:1 (the same
                  scoping rule the nav links above are commented with). */}
              {showThemePicker && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel
                    id={THEME_PICKER_LABEL_ID}
                    className="text-[11px] font-semibold uppercase tracking-wider text-content-muted"
                  >
                    Appearance
                  </DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    aria-labelledby={THEME_PICKER_LABEL_ID}
                    aria-busy={themeSave.pending || undefined}
                    value={theme!.snapshot.preference}
                    onValueChange={(next) => {
                      // The provider serializes and rolls back; the picker neither keeps its own
                      // copy of the choice nor handles the promise — every outcome arrives back
                      // through `preferenceSave`.
                      void setPreference!(next as ThemePreference);
                    }}
                  >
                    {themeOptions.map((option) => {
                      const OptionIcon = option.icon;
                      const checked = theme!.snapshot.preference === option.value;
                      return (
                        <DropdownMenuRadioItem
                          key={option.value}
                          value={option.value}
                          // HOLD THE MENU OPEN while the write is in flight (H1). Radix's
                          // `checkForDefaultPrevented: false` on the radio item means this does
                          // NOT suppress `onValueChange` — only the close. The menu is closed
                          // again by the settlement effect above, on success.
                          onSelect={(event) => event.preventDefault()}
                          // The focus ring now lives in the shared wrapper (`dropdown-menu.tsx`),
                          // so only the icon/label gap is set here.
                          className="gap-2"
                        >
                          <OptionIcon
                            className="h-4 w-4 shrink-0 text-content-muted"
                            aria-hidden="true"
                          />
                          <span className={cn('flex-1', checked && 'font-semibold')}>
                            {option.label}
                          </span>
                        </DropdownMenuRadioItem>
                      );
                    })}
                  </DropdownMenuRadioGroup>
                  {/* SEEN, not just announced. The menu is held open across the write (see the
                      item's `onSelect` below), so both of these are on screen at the moment the
                      outcome arrives rather than a line the user would have to reopen the menu
                      to find. The options stay operable, so choosing again IS the retry. Both
                      messages are TEXT; the live regions further down are the screen-reader half
                      and live OUTSIDE the menu, which the success path closes. */}
                  {themeSave.pending && (
                    <p className="px-2 py-1 text-xs text-content-secondary">
                      Saving your appearance choice…
                    </p>
                  )}
                  {themeSave.error !== null && (
                    <p className="px-2 py-1 text-xs text-status-rejected">
                      {themeSave.error} Choose an option again to retry.
                    </p>
                  )}
                </>
              )}
              {/* Not a menu item: nothing to activate, so it stays out of the arrow-key order.
                  Desktop only — touch devices pinch to zoom. */}
              <DropdownMenuSeparator className="hidden lg:block" />
              <p className="hidden px-2 py-1.5 text-xs text-content-secondary lg:block">
                Text size: <kbd className="font-semibold">{ZOOM_KEY} +</kbd> /{' '}
                <kbd className="font-semibold">{ZOOM_KEY} −</kbd> zooms this browser
              </p>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                onClick={() => logout.mutate()}
              >
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/*
            MOUNTED FOR AS LONG AS THE PICKER EXISTS, with only the text changing: inserting a
            live region at announcement time is the shape screen readers are least reliable
            about, and this pair also has to outlive the menu that triggers it (Radix unmounts
            the menu on close, taking the visible copies with it).

            `aria-live` WITHOUT `role="status"`/`role="alert"`, deliberately. The announcement is
            identical — `aria-live` is the mechanism, the roles are shorthand for it — but this
            bar renders on EVERY authenticated page, so claiming those roles here would put a
            permanent, usually-empty status AND alert region on top of whatever the page itself
            announces: ambiguous for anyone enumerating regions, and it would make every existing
            `getByRole('status')` on a portal page match the chrome as well as the page.
          */}
          {showThemePicker && (
            <>
              <div
                data-theme-preference-status
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
              >
                {themeSave.pending ? 'Saving your appearance choice.' : themeAnnouncement}
              </div>
              <div
                data-theme-preference-error
                aria-live="assertive"
                aria-atomic="true"
                className="sr-only"
              >
                {themeSave.error ?? ''}
              </div>
            </>
          )}

          {/* Mobile hamburger */}
          <button
            type="button"
            // R3c/S2 — same repair as the nav links above (3.83:1 on `#1a1a1a`).
            className="lg:hidden p-2 rounded-md hover:bg-chrome-bg-hover transition-colors outline-none focus-visible:ring-2 focus-visible:ring-highlight"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            // Names the panel only while it is mounted, so it never points at a missing id.
            aria-controls={mobileOpen ? MOBILE_MENU_ID : undefined}
          >
            {mobileOpen ? (
              <X className="h-5 w-5 text-on-chrome" />
            ) : (
              <Menu className="h-5 w-5 text-on-chrome" />
            )}
          </button>
        </div>
      </header>

      {/* Mobile nav dropdown — groups render as labelled sections (no nested menus) */}
      {mobileOpen && (
        <>
          <div
            data-print-hidden
            className="lg:hidden fixed inset-0 top-topnav bg-black/30 z-30"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          {/* carbon-reskin Phase 3 gate (F4). R2 role 6 names "the dark bar/mobile DRAWER", and
              the drawer was missed on the first build: these links carried no component ring at
              all, so they fell through to `globals.css`'s global black outline drawn over
              `bg-chrome-bg` (#1E293B) — 1.43:1, i.e. the whole of navigation at narrow
              viewports had no usable focus indicator. Every link below now takes the same lime
              ring as the bar: #D4FF00 on #1E293B is 12.61 (recomputed in this edit, truncated
              toward zero). This is a dark surface, so the lime scoping rule holds — the white
              dropdown panel above uses the black `ring-ring` instead. */}
          <nav
            id={MOBILE_MENU_ID}
            data-print-hidden
            className="lg:hidden fixed top-topnav left-0 right-0 bg-chrome-bg border-b border-chrome-border z-40 px-4 py-2 max-h-[calc(100vh-56px)] overflow-y-auto"
            role="navigation"
          >
            {navItems.map((entry) => {
              if (entry.kind === 'group') {
                const GroupIcon = entry.icon;
                return (
                  <div key={entry.label} className="py-1">
                    <div className="flex items-center gap-2 px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-chrome-text-muted">
                      <GroupIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span>{entry.label}</span>
                    </div>
                    {entry.items.map((leaf) => {
                      const LeafIcon = leaf.icon;
                      const isActive = isLeafActive(leaf.path);
                      return (
                        <NavLink
                          key={leaf.path}
                          to={leaf.path}
                          end={leaf.path === config.basePath}
                          className={cn(
                            'flex items-center gap-2.5 pl-7 pr-3 py-2.5 rounded-md text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-highlight',
                            isActive
                              ? 'font-semibold bg-chrome-bg-active'
                              : 'text-chrome-text-muted hover:text-on-chrome hover:bg-chrome-bg-hover-soft',
                          )}
                          style={isActive ? { color: config.accentCssValue } : undefined}
                          onClick={() => setMobileOpen(false)}
                        >
                          <LeafIcon className="h-4 w-4 shrink-0" />
                          <span className="flex-1">{leaf.label}</span>
                          {leaf.badge !== undefined && leaf.badge > 0 && (
                            <span className="bg-status-rejected text-white text-[10px] rounded-full px-1 min-w-[16px] text-center font-medium">
                              {leaf.badge}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                );
              }

              const Icon = entry.icon;
              const isActive = isLeafActive(entry.path);
              return (
                <NavLink
                  key={entry.path}
                  to={entry.path}
                  end={entry.path === config.basePath}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-highlight',
                    isActive
                      ? 'font-semibold bg-chrome-bg-active'
                      : 'text-chrome-text-muted hover:text-on-chrome hover:bg-chrome-bg-hover-soft',
                  )}
                  style={isActive ? { color: config.accentCssValue } : undefined}
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{entry.label}</span>
                  {entry.badge !== undefined && entry.badge > 0 && (
                    <span className="bg-status-rejected text-white text-[10px] rounded-full px-1 min-w-[16px] text-center font-medium">
                      {entry.badge}
                    </span>
                  )}
                </NavLink>
              );
            })}

            {settingsPath && (
              <NavLink
                to={settingsPath}
                className={cn(
                  'flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-highlight',
                  isLeafActive(settingsPath)
                    ? 'font-semibold bg-chrome-bg-active'
                    : 'text-chrome-text-muted hover:text-on-chrome hover:bg-chrome-bg-hover-soft',
                )}
                style={isLeafActive(settingsPath) ? { color: config.accentCssValue } : undefined}
                onClick={() => setMobileOpen(false)}
              >
                <Settings className="h-4 w-4 shrink-0" />
                <span>Settings</span>
              </NavLink>
            )}

            {/* `min-h-11` (44 px): `py-2.5` alone leaves this row short of the touch floor. */}
            {helpPath && (
              <NavLink
                to={helpPath}
                className={cn(
                  'flex min-h-11 items-center gap-2.5 px-3 py-2.5 rounded-md text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-highlight',
                  isLeafActive(helpPath)
                    ? 'font-semibold bg-chrome-bg-active'
                    : 'text-chrome-text-muted hover:text-on-chrome hover:bg-chrome-bg-hover-soft',
                )}
                style={isLeafActive(helpPath) ? { color: config.accentCssValue } : undefined}
                onClick={() => setMobileOpen(false)}
              >
                <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>Help</span>
              </NavLink>
            )}
          </nav>
        </>
      )}
    </>
  );
}
